// src/media-upload.ts
import AwsS3 from "@uppy/aws-s3";
import Uppy from "@uppy/core";
var MultipartUploadError = class extends Error {
  mediaId;
  uploadId;
  status;
  constructor(message, details = {}) {
    super(message, { cause: details.cause });
    this.name = "MultipartUploadError";
    this.mediaId = details.mediaId;
    this.uploadId = details.uploadId;
    this.status = details.status;
  }
};
var PantheonApiError = class extends Error {
  constructor(message) {
    super(message);
    this.name = "PantheonApiError";
  }
};
function createPantheonMultipartUploadApi({
  apiEndpoint,
  assessmentId,
  createHeaders,
  credentials = "include"
}) {
  return {
    async initMultipartUpload(args) {
      const resolvedAssessmentId = typeof assessmentId === "function" ? assessmentId() : assessmentId;
      if (resolvedAssessmentId == null) {
        throw new Error("Assessment ID not available");
      }
      const response = await fetch(`${apiEndpoint}/api/media/multipart`, {
        method: "POST",
        headers: createHeaders("application/json"),
        credentials,
        body: JSON.stringify({
          filename: args.filename,
          contentType: args.contentType,
          assessmentId: resolvedAssessmentId
        })
      });
      if (!response.ok) {
        throw new PantheonApiError(
          `Failed to create multipart upload: ${response.status}`
        );
      }
      return response.json();
    },
    async signPart(args) {
      const response = await fetch(
        `${apiEndpoint}/api/media/${encodeURIComponent(args.mediaId)}/multipart/parts`,
        {
          method: "POST",
          headers: createHeaders("application/json"),
          credentials,
          body: JSON.stringify({
            uploadId: args.uploadId,
            partNumber: args.partNumber
          }),
          signal: args.signal
        }
      );
      if (!response.ok) {
        throw new PantheonApiError(`Failed to sign part: ${response.status}`);
      }
      return response.json();
    },
    async listParts(args) {
      const url = new URL(
        `${apiEndpoint}/api/media/${encodeURIComponent(args.mediaId)}/multipart/parts`
      );
      url.searchParams.set("uploadId", args.uploadId);
      const response = await fetch(url.toString(), {
        method: "GET",
        headers: createHeaders(),
        credentials,
        signal: args.signal
      });
      if (!response.ok) {
        throw new PantheonApiError(`Failed to list parts: ${response.status}`);
      }
      return response.json();
    },
    async completeMultipartUpload(args) {
      const response = await fetch(
        `${apiEndpoint}/api/media/${encodeURIComponent(args.mediaId)}/multipart/complete`,
        {
          method: "POST",
          headers: createHeaders("application/json"),
          credentials,
          body: JSON.stringify({
            uploadId: args.uploadId,
            parts: args.parts
          }),
          signal: args.signal
        }
      );
      if (!response.ok) {
        throw new PantheonApiError(
          `Failed to complete multipart upload: ${response.status}`
        );
      }
      return response.json();
    },
    async abortMultipartUpload(args) {
      const url = new URL(
        `${apiEndpoint}/api/media/${encodeURIComponent(args.mediaId)}/multipart`
      );
      url.searchParams.set("uploadId", args.uploadId);
      const response = await fetch(url.toString(), {
        method: "DELETE",
        headers: createHeaders(),
        credentials,
        signal: args.signal
      });
      if (!response.ok && response.status !== 204) {
        throw new PantheonApiError(
          `Failed to abort multipart upload: ${response.status}`
        );
      }
    }
  };
}
function createMultipartUpload(args) {
  const { file, onProgress, retryDelays } = args;
  const api = "api" in args ? args.api : createPantheonMultipartUploadApi(args);
  const uppy = new Uppy({
    autoProceed: false,
    allowMultipleUploadBatches: false
  });
  let mediaId = null;
  let uploadId = null;
  let resolvedSrc = null;
  let pantheonError = null;
  const rememberPantheonError = (err) => {
    if (err instanceof PantheonApiError) pantheonError = err;
    throw err;
  };
  let lastS3Status;
  uppy.use(AwsS3, {
    shouldUseMultipart: true,
    ...retryDelays != null ? { retryDelays } : {},
    async createMultipartUpload(uppyFile) {
      const result = await api.initMultipartUpload({
        filename: uppyFile.name ?? file.name,
        // File.type is "" for unknown MIME types, so ?? alone is not enough.
        contentType: [uppyFile.type, file.type].find((t) => t != null && t !== "") ?? "application/octet-stream"
      }).catch(rememberPantheonError);
      mediaId = result.mediaId;
      uploadId = result.uploadId;
      return { key: result.mediaId, uploadId: result.uploadId };
    },
    async signPart(_file, { uploadId: uploadId2, key, partNumber, signal }) {
      const { url, expires } = await api.signPart({
        mediaId: key,
        uploadId: uploadId2,
        partNumber,
        signal
      }).catch(rememberPantheonError);
      return {
        method: "PUT",
        url,
        expires: secondsUntil(expires)
      };
    },
    async listParts(_file, { key, uploadId: uploadId2, signal }) {
      return api.listParts({ mediaId: key, uploadId: uploadId2, signal }).catch(rememberPantheonError);
    },
    async completeMultipartUpload(_file, { key, uploadId: uploadId2, parts, signal }) {
      const completeParts = [];
      for (const part of parts) {
        if (typeof part.PartNumber === "number" && typeof part.ETag === "string") {
          completeParts.push({
            PartNumber: part.PartNumber,
            ETag: part.ETag
          });
        }
      }
      completeParts.sort((a, b) => a.PartNumber - b.PartNumber);
      const { src } = await api.completeMultipartUpload({
        mediaId: key,
        uploadId: uploadId2,
        parts: completeParts,
        signal
      }).catch(rememberPantheonError);
      resolvedSrc = src;
      return { location: src };
    },
    async abortMultipartUpload(_file, { key, uploadId: uploadId2, signal }) {
      await api.abortMultipartUpload({
        mediaId: key,
        uploadId: uploadId2,
        signal
      }).catch(rememberPantheonError);
    }
  });
  if (onProgress) {
    uppy.on("upload-progress", (_uppyFile, progress) => {
      if (progress.bytesTotal != null && progress.bytesTotal > 0) {
        onProgress(progress.bytesUploaded / progress.bytesTotal * 100);
      }
    });
  }
  uppy.on("upload-error", (_uppyFile, _error, response) => {
    if (typeof response?.status === "number") lastS3Status = response.status;
  });
  uppy.addFile({
    name: file.name,
    type: file.type,
    data: file,
    source: "multipart-upload"
  });
  function getResult() {
    if (mediaId == null || resolvedSrc == null) {
      throw new Error("Multipart upload completed without mediaId or src");
    }
    return {
      id: mediaId,
      url: resolvedSrc,
      name: file.name,
      file_size: file.size
    };
  }
  let cleanedUp = false;
  function cleanup() {
    if (cleanedUp) return;
    cleanedUp = true;
    uppy.cancelAll();
    uppy.destroy();
  }
  function toUploadError(failure) {
    if (pantheonError != null) return pantheonError;
    const rawError = failure.error;
    const message = rawError instanceof Error ? rawError.message : typeof rawError === "string" && rawError !== "" ? rawError : "Multipart upload failed";
    return new MultipartUploadError(message, {
      mediaId: mediaId ?? void 0,
      uploadId: uploadId ?? void 0,
      status: lastS3Status,
      cause: rawError
    });
  }
  async function run(uppyFn) {
    pantheonError = null;
    lastS3Status = void 0;
    const result = await uppyFn();
    const failure = result?.failed?.[0];
    if (failure) {
      throw toUploadError(failure);
    }
    const media = getResult();
    cleanup();
    return media;
  }
  return {
    start: () => run(() => uppy.upload()),
    resume: () => run(() => uppy.retryAll()),
    cancel: cleanup
  };
}
function secondsUntil(isoTimestamp) {
  const expiresAt = Date.parse(isoTimestamp);
  if (Number.isNaN(expiresAt)) return 0;
  return Math.max(0, Math.floor((expiresAt - Date.now()) / 1e3));
}
export {
  MultipartUploadError,
  createMultipartUpload,
  createPantheonMultipartUploadApi
};
//# sourceMappingURL=index.mjs.map